// Lib
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { debounce } from 'lodash';

// Utils
import {
    getNativeAppModeForFormFactor,
    getWindowFillsScreen,
    getWindowFormFactor,
    isFormFactorWindowDerived,
} from '../utils/platformFormFactorUtils';

// Selectors
import {
    getCapacitorPlatformSelector,
    getPlatformFormFactorSelector,
    selectWindowFillsScreen,
} from '../platformSelector';

// Actions
import { updatePlatformDetails } from '../platformActions';

// Types
import { PlatformDetails } from '../../../common/platform/platformTypes';

// Long enough to skip the intermediate sizes of a Stage Manager drag, short enough to feel immediate once it settles.
export const WINDOW_GEOMETRY_RESIZE_DEBOUNCE_MS = 200;

/**
 * Keeps the window-derived platform details current on the iOS app, where Stage Manager and folding
 * devices resize the window mid-session: the form factor, and whether the window fills the screen.
 * The application mode only follows a form-factor transition, so a manual "switch canvas view"
 * survives rotations and resizes that stay within the same form factor.
 */
export default function useWindowGeometryListener() {
    const capacitorPlatform = useSelector(getCapacitorPlatformSelector);
    const currentFormFactor = useSelector(getPlatformFormFactorSelector);
    const currentWindowFillsScreen = useSelector(selectWindowFillsScreen);

    const dispatch = useDispatch();

    useEffect(() => {
        if (!isFormFactorWindowDerived(capacitorPlatform)) return;

        const updatePlatformDetailsFromWindow = debounce(() => {
            const changedDetails: Partial<PlatformDetails> = {};

            const nextWindowFillsScreen = getWindowFillsScreen(
                window.innerWidth,
                window.innerHeight,
                screen.width,
                screen.height,
            );
            if (nextWindowFillsScreen !== currentWindowFillsScreen) {
                changedDetails.windowFillsScreen = nextWindowFillsScreen;
            }

            const nextFormFactor = getWindowFormFactor(window.innerWidth, window.innerHeight);
            if (nextFormFactor !== currentFormFactor) {
                changedDetails.formFactor = nextFormFactor;
                changedDetails.appMode = getNativeAppModeForFormFactor(nextFormFactor);
            }

            if (Object.keys(changedDetails).length === 0) return;

            dispatch(updatePlatformDetails(changedDetails));
        }, WINDOW_GEOMETRY_RESIZE_DEBOUNCE_MS);

        // The window may have been resized between the platform details being computed and this mounting
        updatePlatformDetailsFromWindow();
        window.addEventListener('resize', updatePlatformDetailsFromWindow);

        return () => {
            updatePlatformDetailsFromWindow.cancel();
            window.removeEventListener('resize', updatePlatformDetailsFromWindow);
        };
    }, [capacitorPlatform, currentFormFactor, currentWindowFillsScreen, dispatch]);
}
